'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { cn, debounce } from '@/lib/utils';
import { useBrowseFilters, type SortKey } from '@/lib/hooks/useBrowseFilters';

/**
 * SearchSortRow — the row above Browse results.
 *
 * Spec: browse.html `.browse-bar` — 3 columns (search · sort · count).
 * Search input debounced 200ms → writes to `?q=`. `?focus=search` from
 * Cmd+K routes focuses this input on mount.
 */

const SORT_OPTS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'sources', label: 'Most sources' },
  { value: 'shortest', label: 'Shortest read' },
];

export interface SearchSortRowProps {
  resultCount: number;
}

export function SearchSortRow({ resultCount }: SearchSortRowProps) {
  const { filters, set } = useBrowseFilters();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localQ, setLocalQ] = useState(filters.q);

  // Sync local input state when URL `q` changes from outside (e.g. back button).
  useEffect(() => {
    setLocalQ(filters.q);
  }, [filters.q]);

  // Debounced URL write — avoids a history entry per keystroke.
  const debouncedSetQ = useMemo(
    () => debounce((v: string) => set({ q: v }), 200),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    // Focus on Cmd+K navigation from Header
    if (filters.focus === 'search' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [filters.focus]);

  // Global Cmd+K on this page focuses the input directly.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
          return;
        }
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="max-w-container mx-auto px-10 mt-4">
      <div className="flex flex-col md:grid md:grid-cols-[1fr_auto_auto] gap-4 md:gap-5 items-center p-[14px_20px] rounded-xl bg-white border border-border">
        <div className="flex items-center gap-2.5 w-full">
          <Search className="w-4 h-4 text-slate shrink-0" strokeWidth={2} />
          <input
            ref={inputRef}
            type="search"
            value={localQ}
            onChange={(e) => {
              setLocalQ(e.target.value);
              debouncedSetQ(e.target.value);
            }}
            placeholder="Search titles, supplements, studies…"
            className={cn(
              'flex-1 bg-transparent border-0 outline-0',
              'font-ui text-[14px] text-ink placeholder:text-slate'
            )}
            aria-label="Search articles"
          />
          <kbd className="font-mono text-[10px] text-slate bg-neutral-50 border border-border rounded-[4px] px-[5px] py-px tracking-[0.04em]">
            ⌘K
          </kbd>
        </div>

        <div className="flex items-center gap-2 border-l md:pl-5 pl-0 border-border">
          <span className="font-mono text-[11px] tracking-[0.04em] text-slate">Sort</span>
          <select
            value={filters.sort}
            onChange={(e) => set({ sort: e.target.value as SortKey })}
            className={cn(
              'font-ui text-[12.5px] text-ink',
              'px-2 py-[5px] rounded-sm cursor-pointer',
              'bg-white border border-border',
              'focus:outline-none focus:border-aviado-blue focus:shadow-focus'
            )}
          >
            {SORT_OPTS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="font-mono text-[11px] tracking-[0.04em] text-slate md:border-l md:pl-5 md:border-border">
          <strong className="text-warm-navy font-medium tabular-nums">{resultCount}</strong>{' '}
          {resultCount === 1 ? 'result' : 'results'}
        </div>
      </div>
    </div>
  );
}

export default SearchSortRow;
